// /competitors — Saved competitors library. Canonical surface for
// managing the brands a user tracks. Ad Strategist runs read from here
// instead of doing fresh Apify scrapes on every analysis.
//
// Card grid pattern matches My Creatives: hover-reveal action buttons,
// image-forward layout (no images here — competitors are text-only —
// but the same hover ergonomics keep the UI feel consistent).

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayoutV2 from '@/components/feature/AppLayoutV2';
import {
  addCompetitor,
  addCompetitorPage,
  searchAdvertisers,
  deleteCompetitor,
  listCompetitors,
  suggestCompetitors,
  type SavedCompetitor,
  type CompetitorSuggestion,
  type AdvertiserPage,
} from '@/lib/api';
import { useConfirm } from '@/hooks/useConfirm';
import { useBrand } from '@/hooks/useBrand';
import { useCredits } from '@/hooks/useCredits';

function relativeTime(iso: string | null): string {
  if (!iso) return 'Never';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const ms = Math.max(0, now - then);
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
}

export default function CompetitorsPage() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { activeBrand } = useBrand();
  // Free/Starter keep whatever the onboarding pull produced (read-only), but
  // Add + Refresh are Pro (each is a paid Apify/ScrapeCreators pull). Backend
  // enforces the same gate on the refresh/search endpoints.
  const { planTier } = useCredits();
  const isPro = planTier === 'pro' || planTier === 'business';
  const [competitors, setCompetitors] = useState<SavedCompetitor[]>([]);
  // Optimistic placeholders shown the instant a competitor is picked/added,
  // while the backend fetches their ads (~5-15s). Replaced by the real card
  // on resolve so the user always sees the competitor they just chose.
  const [pending, setPending] = useState<{ key: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Add flow — paste URL OR pick from suggestions
  const [addOpen, setAddOpen] = useState(false);


  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listCompetitors();
      setCompetitors(result.competitors);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load competitors');
    } finally {
      setLoading(false);
    }
  }, [activeBrand?.id]);

  useEffect(() => { void reload(); }, [reload]);

  // Auto-dismiss notices
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  const handleDelete = async (comp: SavedCompetitor) => {
    const ok = await confirm({
      title: `Remove ${comp.label}?`,
      message: 'This stops tracking them. You can add them back any time.',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteCompetitor(comp.id);
      setCompetitors((prev) => prev.filter((c) => c.id !== comp.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  return (
    <AppLayoutV2>
      <div className="max-w-[1500px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <i className="ri-spy-line text-primary text-2xl" />
              <h1 className="font-display font-bold text-dark text-2xl">Competitors</h1>
              <span className="ml-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-bold">
                {competitors.length}/4
              </span>
            </div>
            <p className="text-dark-muted text-sm">
              Brands you track. Ad Strategist reads from this list, so analyses
              run fast. Refresh when you want a fresh look at their ads.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
            {/* Refresh all removed June 8 — bulk refresh was slow + noisy.
                Users now refresh individually from each competitor's
                detail view. */}
            <button
              onClick={() => (isPro ? setAddOpen(true) : navigate('/checkout'))}
              disabled={isPro && competitors.length >= 4}
              title={!isPro
                ? 'Adding & refreshing competitors is a Pro feature'
                : competitors.length >= 4
                ? 'You can track up to 4 competitors — remove one to add another'
                : undefined}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-primary hover:bg-primary-hover text-white text-xs font-bold shadow-md shadow-primary/25 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {!isPro
                ? <><i className="ri-vip-crown-line" /> Add on Pro</>
                : <><i className="ri-add-line" /> {competitors.length >= 4 ? 'Tracking 4/4' : 'Add competitor'}</>}
            </button>
          </div>
        </div>

        {/* Notice / Error banners */}
        {notice && (
          <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 text-sm font-medium">
            <i className="ri-check-line" />
            {notice}
          </div>
        )}
        {error && (
          <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 text-red-700 border border-red-200 text-sm font-medium">
            <i className="ri-error-warning-line" />
            {error}
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-dark-muted">
            <i className="ri-loader-4-line animate-spin text-2xl mr-2" />
            Loading your competitors…
          </div>
        ) : competitors.length === 0 && pending.length === 0 ? (
          <div className="rounded-2xl glass-stroke bg-white/85 px-6 py-12 text-center">
            <i className="ri-spy-line text-4xl text-dark-muted/60" />
            <p className="font-display font-bold text-dark text-lg mt-3">No competitors saved yet</p>
            <p className="text-sm text-dark-muted mt-1 max-w-md mx-auto">
              Add the brands you want Ad Strategist to track. Each is checked
              against the public Meta Ad Library so you can see what's working.
            </p>
            <button
              onClick={() => (isPro ? setAddOpen(true) : navigate('/checkout'))}
              className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all cursor-pointer"
            >
              {!isPro
                ? <><i className="ri-vip-crown-line" /> Add competitors on Pro</>
                : <><i className="ri-add-line" /> Add your first competitor</>}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
            {pending.map((p) => (
              <PendingCompetitorCard key={p.key} name={p.name} />
            ))}
            {competitors.map((c) => (
              <CompetitorCard
                key={c.id}
                competitor={c}
                onView={() => navigate(`/competitors/${c.id}`)}
                onDelete={() => handleDelete(c)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add modal */}
      {addOpen && (
        <AddCompetitorModal
          existing={competitors}
          onClose={() => { setAddOpen(false); void reload(); }}
          onPendingStart={(key, name) => setPending((prev) => [...prev, { key, name }])}
          onPendingResolve={(key, real) => {
            setPending((prev) => prev.filter((p) => p.key !== key));
            if (real) {
              setCompetitors((prev) => (prev.some((c) => c.id === real.id) ? prev : [real, ...prev]));
              setNotice(`Added ${real.label || 'competitor'}.`);
            }
          }}
        />
      )}

    </AppLayoutV2>
  );
}

// ----- Card -----

const CARD_COLORS = [
  'bg-rose-500', 'bg-pink-500', 'bg-fuchsia-500', 'bg-purple-500',
  'bg-violet-500', 'bg-indigo-500', 'bg-blue-500', 'bg-sky-500',
  'bg-cyan-500', 'bg-teal-500', 'bg-emerald-500', 'bg-amber-500',
];

// Optimistic placeholder shown while the backend fetches a newly-added
// competitor's ads — same footprint as CompetitorCard so the grid doesn't
// jump when the real card replaces it.
function PendingCompetitorCard({ name }: { name: string }) {
  const initial = (name || '?').trim().charAt(0).toUpperCase() || '?';
  const color = CARD_COLORS[initial.charCodeAt(0) % CARD_COLORS.length];
  return (
    <div className="relative rounded-xl bg-white border border-surface-dark/40 overflow-hidden">
      <div className={`relative aspect-[16/9] flex items-center justify-center ${color}`}>
        <span className="text-white font-display font-bold text-5xl opacity-80">{initial}</span>
      </div>
      <div className="p-3">
        <p className="font-display font-bold text-dark text-sm truncate">{name}</p>
        <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-dark-muted">
          <i className="ri-loader-4-line animate-spin text-primary" /> Loading ads&hellip;
        </p>
      </div>
    </div>
  );
}

function CompetitorCard({
  competitor,
  onView,
  onDelete,
}: {
  competitor: SavedCompetitor;
  onView: () => void;
  onDelete: () => void;
}) {
  const hasAds = competitor.active_ads_count > 0;
  // Only show the "View ads" button when there's actually visual creative
  // to render. Text-only ads still appear on the detail page (and are
  // useful for copy inspiration) but the list page's button promises
  // visual ads — hiding it avoids the dead-click broken-image experience.
  const hasVisualAds = (competitor.image_ads_count ?? 0) > 0;

  // Initial letter for the colored placeholder when no logo found.
  // Deterministic color per first-letter so the same competitor always
  // shows the same chip color across renders.
  const initial = (competitor.label || competitor.facebook_handle || '?')
    .trim().charAt(0).toUpperCase() || '?';
  const COLORS = [
    'bg-rose-500', 'bg-pink-500', 'bg-fuchsia-500', 'bg-purple-500',
    'bg-violet-500', 'bg-indigo-500', 'bg-blue-500', 'bg-sky-500',
    'bg-cyan-500', 'bg-teal-500', 'bg-emerald-500', 'bg-amber-500',
  ];
  const colorIdx = initial.charCodeAt(0) % COLORS.length;
  const placeholderColor = COLORS[colorIdx];

  return (
    // Whole card is a button now — clicking anywhere navigates to the
    // detail page (which loads ads). Delete sits as a hover-revealed
    // pill on the hero, stopPropagation so it doesn't navigate.
    <div
      role="button"
      tabIndex={0}
      onClick={onView}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onView();
        }
      }}
      className="group relative rounded-xl bg-white border border-surface-dark/40 overflow-hidden transition-all hover:shadow-lg hover:shadow-primary/10 hover:border-primary/40 cursor-pointer"
    >
      {/* Logo hero — logo centered, falls back to a giant initial letter
          on a brand-tinted background. Logos read reliably across all
          competitors; the og:image experiment was too spotty (most
          Shopify storefronts skip the meta tag entirely). */}
      <div className={`relative aspect-[16/9] flex items-center justify-center overflow-hidden ${
        !competitor.logo_url ? placeholderColor : 'bg-white'
      }`}>
        {competitor.logo_url ? (
          <img
            src={competitor.logo_url}
            alt={competitor.label}
            className="max-w-[60%] max-h-[60%] object-contain"
            loading="lazy"
            onError={(e) => {
              // Source died — hide the <img> and flip the container to
              // the colored initial-letter placeholder.
              const img = e.currentTarget as HTMLImageElement;
              img.style.display = 'none';
              const parent = img.parentElement;
              if (parent) {
                parent.className = parent.className.replace('bg-white', placeholderColor);
                const span = document.createElement('span');
                span.className = 'text-white font-display font-bold text-5xl';
                span.textContent = initial;
                parent.appendChild(span);
              }
            }}
          />
        ) : (
          <span className="text-white font-display font-bold text-5xl">
            {initial}
          </span>
        )}
        {/* Ads-count chip overlay, top-right */}
        <span className={`absolute top-2 right-2 px-2 py-0.5 rounded-md text-[11px] font-bold backdrop-blur-sm ${
          hasAds
            ? 'bg-emerald-500/90 text-white'
            : 'bg-white/85 text-dark-muted'
        }`}>
          {hasAds
            ? `${competitor.active_ads_count}${competitor.active_ads_count >= 20 ? '+' : ''} ad${competitor.active_ads_count === 1 ? '' : 's'}`
            : 'No ads'}
        </span>
        {/* Delete — hover-revealed pill, top-left. stopPropagation so the
            card's onClick doesn't fire. */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          title="Remove"
          aria-label="Remove"
          className="absolute top-2 left-2 inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/90 backdrop-blur-sm text-red-600 hover:bg-red-500 hover:text-white shadow-sm cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <i className="ri-delete-bin-line text-sm" />
        </button>
      </div>

      <div className="p-3">
        <h3 className="font-display font-bold text-dark text-sm truncate">
          {competitor.label}
        </h3>
        {competitor.facebook_handle && (
          <p className="text-[11px] text-dark-muted truncate">
            facebook.com/{competitor.facebook_handle}
          </p>
        )}
        <p className="text-[11px] text-dark-muted mt-1 mb-3">
          <i className="ri-time-line mr-1" />
          Refreshed {relativeTime(competitor.last_refreshed_at)}
        </p>

        {/* View ads — primary action when there's visual creative to
            render. Same `onView` target as the whole-card click, but
            gives users a clear CTA. stopPropagation isn't needed since
            both handlers do the same nav, but kept for symmetry with
            the Delete button. When there are no visual ads, swap for a
            muted text-only label that keeps the card height stable. */}
        {hasVisualAds ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onView();
            }}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-primary hover:bg-primary-hover text-white text-xs font-bold transition-all cursor-pointer"
            title="View their cached ads"
          >
            <i className="ri-eye-line" /> View ads
          </button>
        ) : (
          <span
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-surface-dark/15 text-dark-muted text-xs font-bold cursor-default"
            title={hasAds ? 'No visual ads — these are text-only' : 'No ads found'}
          >
            <i className="ri-image-off-line" /> {hasAds ? 'Text only' : 'No ads'}
          </span>
        )}
      </div>
    </div>
  );
}

// ----- Add modal -----

function AddCompetitorModal({
  existing,
  onClose,
  onPendingStart,
  onPendingResolve,
}: {
  existing: SavedCompetitor[];
  onClose: () => void;
  onPendingStart: (key: string, name: string) => void;
  onPendingResolve: (key: string, real: SavedCompetitor | null) => void;
}) {
  // Adds are async (backend fetches ads first) — show an optimistic
  // placeholder on the list the instant the user picks, then resolve it.
  const startPending = (name: string): string => {
    const key = `p-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    onPendingStart(key, name || 'Competitor');
    return key;
  };

  const [tab, setTab] = useState<'url' | 'suggest'>('url');
  const [urlInput, setUrlInput] = useState('');
  const [labelInput, setLabelInput] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Suggestions state
  const [suggestions, setSuggestions] = useState<CompetitorSuggestion[]>([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestedOnce, setSuggestedOnce] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [pickedQueries, setPickedQueries] = useState<Set<string>>(new Set());

  // Search-by-name state (Jul 23) — type a name → pick the exact Ad Library page.
  const [searchResults, setSearchResults] = useState<AdvertiserPage[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  // Holds the searched term when name-search returns 0 pages — drives the
  // actionable "paste the Facebook URL instead" panel (distinct from a real
  // error). Our search only sees advertisers with active, copy-matching ads;
  // image-only advertisers or ones not currently advertising won't surface.
  const [noMatch, setNoMatch] = useState<string | null>(null);
  const [pickedPageIds, setPickedPageIds] = useState<Set<string>>(new Set());

  const existingQueries = new Set(existing.map((c) => c.query));

  // Esc closes
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const handleAddUrl = async () => {
    if (!urlInput.trim()) return;
    setAdding(true);
    setError(null);
    const key = startPending(labelInput.trim() || urlInput.trim());
    try {
      const result = await addCompetitor({
        query: urlInput.trim(),
        label: labelInput.trim() || undefined,
      });
      onPendingResolve(key, result.competitor);
      setUrlInput('');
      setLabelInput('');
      onClose();
    } catch (err) {
      onPendingResolve(key, null);
      setError(err instanceof Error ? err.message : 'Could not add competitor');
    } finally {
      setAdding(false);
    }
  };

  // A pasted URL adds directly (old flow); a bare name searches the Ad Library.
  const looksLikeUrl = (s: string) =>
    /^https?:\/\//i.test(s) || /facebook\.com/i.test(s) || /\.[a-z]{2,}(\/|$)/i.test(s);

  const handleSearch = async () => {
    const term = urlInput.trim();
    if (!term) return;
    if (looksLikeUrl(term)) { await handleAddUrl(); return; }
    setSearchLoading(true);
    setSearchError(null);
    setNoMatch(null);
    setSearchResults([]);
    try {
      const res = await searchAdvertisers(term);
      setSearchResults(res.pages);
      if (res.pages.length === 0) setNoMatch(term);
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  const handlePickPage = async (page: AdvertiserPage) => {
    setPickedPageIds((prev) => new Set(prev).add(page.page_id));
    const key = startPending(page.name);
    try {
      const result = await addCompetitorPage({ pageId: page.page_id, name: page.name });
      onPendingResolve(key, result.competitor);
    } catch (err) {
      onPendingResolve(key, null);
      setPickedPageIds((prev) => {
        const next = new Set(prev);
        next.delete(page.page_id);
        return next;
      });
      setSearchError(err instanceof Error ? err.message : 'Could not add');
    }
  };

  const handleSuggest = async () => {
    setSuggestLoading(true);
    setSuggestError(null);
    setSuggestedOnce(true);
    try {
      const result = await suggestCompetitors();
      setSuggestions(result.suggestions);
    } catch (err) {
      setSuggestError(err instanceof Error ? err.message : 'Suggestions failed');
    } finally {
      setSuggestLoading(false);
    }
  };

  const handlePickSuggestion = async (sug: CompetitorSuggestion) => {
    setPickedQueries((prev) => new Set(prev).add(sug.query));
    const key = startPending(sug.name);
    try {
      // Prefer the page-scoped add (exact advertiser → ALL their ads). Fall
      // back to the name query only when the suggestion couldn't be resolved
      // to a page id.
      const result = sug.page_id
        ? await addCompetitorPage({ pageId: sug.page_id, name: sug.name })
        : await addCompetitor({ query: sug.query, label: sug.name });
      onPendingResolve(key, result.competitor);
    } catch (err) {
      onPendingResolve(key, null);
      console.error('Add suggestion failed', err);
      // Roll back the picked state so user can retry
      setPickedQueries((prev) => {
        const next = new Set(prev);
        next.delete(sug.query);
        return next;
      });
      setError(err instanceof Error ? err.message : 'Could not add');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-2xl glass-stroke bg-white shadow-2xl shadow-primary/15"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 border border-surface-dark/30 text-dark-muted hover:text-dark hover:bg-white shadow-sm flex items-center justify-center cursor-pointer transition-colors"
          aria-label="Close"
        >
          <i className="ri-close-line text-lg" />
        </button>

        <div className="px-6 pt-5 pb-4 border-b border-surface-dark/20">
          <h3 className="font-display font-bold text-dark text-base">Add competitor</h3>
          <p className="text-xs text-dark-muted mt-0.5">
            Search a brand and pick the exact advertiser page — or let AI suggest competitors from your brand.
          </p>
        </div>

        {/* Tab switcher */}
        <div className="px-6 pt-4">
          <div className="flex gap-2 p-1 rounded-full bg-surface-dark/15 sm:w-auto sm:inline-flex">
            <button
              onClick={() => setTab('url')}
              className={`px-4 py-1.5 rounded-full text-sm font-bold transition-all cursor-pointer ${
                tab === 'url' ? 'bg-primary text-white shadow-sm' : 'text-dark-muted hover:text-dark'
              }`}
            >
              <i className="ri-search-line mr-1" /> Search
            </button>
            <button
              onClick={() => setTab('suggest')}
              className={`px-4 py-1.5 rounded-full text-sm font-bold transition-all cursor-pointer ${
                tab === 'suggest' ? 'bg-primary text-white shadow-sm' : 'text-dark-muted hover:text-dark'
              }`}
            >
              <i className="ri-sparkling-2-line mr-1" /> Suggest
            </button>
          </div>
        </div>

        <div className="p-6">
          {tab === 'url' ? (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-dark mb-1.5">
                  Search by brand name
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') void handleSearch(); }}
                    placeholder="MAC Cosmetics   ·   or paste a Facebook / website URL"
                    className="flex-1 px-3 py-2 rounded-lg border border-dark-muted/40 text-sm focus:border-primary outline-none"
                    autoFocus
                  />
                  <button
                    onClick={handleSearch}
                    disabled={!urlInput.trim() || searchLoading || adding}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {searchLoading || adding
                      ? <i className="ri-loader-4-line animate-spin" />
                      : <i className="ri-search-line" />}
                    Search
                  </button>
                </div>
                <p className="text-xs text-dark-muted mt-1">
                  Type the brand's <strong>full name</strong> (e.g. &ldquo;MAC
                  Cosmetics&rdquo;, not &ldquo;mac&rdquo;) — we find the exact
                  advertiser page in the Meta Ad Library. Or paste a Facebook /
                  website URL to add directly.
                </p>
              </div>

              {(searchError || error) && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                  {searchError || error}
                </div>
              )}

              {noMatch && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
                  <p className="text-sm font-bold text-amber-900 flex items-center gap-1.5">
                    <i className="ri-information-line" /> No active-ad match for &ldquo;{noMatch}&rdquo;
                  </p>
                  <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                    We only surface advertisers currently running ads with matching text.
                    Brands with image-only ads — or ones not advertising right now — won&rsquo;t
                    appear. If you can see them in Meta&rsquo;s Ad Library, add them directly:
                  </p>
                  <ol className="text-xs text-amber-800 mt-2 space-y-0.5 list-decimal list-inside">
                    <li>Open them in Meta&rsquo;s Ad Library</li>
                    <li>Copy their Facebook page URL</li>
                    <li>Paste it in the box above and hit Search</li>
                  </ol>
                  <a
                    href={`https://www.facebook.com/ads/library/?active_status=all&ad_type=all&country=US&q=${encodeURIComponent(noMatch)}&search_type=keyword_unordered&media_type=all`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold cursor-pointer"
                  >
                    <i className="ri-external-link-line" /> Open Meta Ad Library
                  </a>
                </div>
              )}

              {searchLoading ? (
                <div className="flex items-center justify-center gap-2 py-8 text-dark-muted text-sm">
                  <i className="ri-loader-4-line animate-spin text-primary text-lg" />
                  Searching the Meta Ad Library…
                  <span className="text-xs text-dark-muted/70 ml-auto">~5-15s</span>
                </div>
              ) : searchResults.length > 0 ? (
                <div className="space-y-2 max-h-[44vh] overflow-y-auto pr-1">
                  {searchResults.map((p) => {
                    const isAdded = pickedPageIds.has(p.page_id);
                    return (
                      <div
                        key={p.page_id}
                        className="rounded-lg bg-white border border-surface-dark/30 px-3 py-2.5 flex items-center gap-3 hover:border-primary/40 transition-colors"
                      >
                        <div className="w-10 h-10 flex-shrink-0 rounded-lg bg-surface overflow-hidden flex items-center justify-center">
                          {p.thumbnail_url ? (
                            // eslint-disable-next-line jsx-a11y/alt-text
                            <img src={p.thumbnail_url} className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <i className="ri-image-line text-dark-muted/50" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-dark truncate">
                            {p.name || 'Unknown advertiser'}
                          </p>
                          <p className="text-xs text-dark-muted">
                            {p.active_ad_count} active ad{p.active_ad_count === 1 ? '' : 's'}
                          </p>
                        </div>
                        <button
                          onClick={() => handlePickPage(p)}
                          disabled={isAdded}
                          className={`flex-shrink-0 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
                            isAdded
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default'
                              : 'bg-primary hover:bg-primary-hover text-white shadow-sm'
                          }`}
                        >
                          {isAdded ? (
                            <><i className="ri-check-line" /> Added</>
                          ) : (
                            <><i className="ri-add-line" /> Add</>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : null}

              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-full border border-surface-dark/40 text-dark text-sm font-semibold hover:bg-surface transition-all cursor-pointer"
                >
                  {(searchResults.length > 0 || pickedPageIds.size > 0) ? 'Done' : 'Cancel'}
                </button>
              </div>
            </div>
          ) : (
            <div>
              {suggestions.length === 0 && !suggestLoading ? (
                <div className="text-center py-6">
                  {suggestedOnce && !suggestError ? (
                    <p className="text-sm text-dark-muted mb-3">
                      None of your likely competitors are <strong>currently running Meta ads</strong>,
                      so there&rsquo;d be nothing to analyze. Try <strong>Search</strong> to add a
                      specific advertiser by name or URL, or run Suggest again.
                    </p>
                  ) : (
                    <p className="text-sm text-dark-muted mb-3">
                      We&rsquo;ll suggest competitors that are <strong>currently running Meta ads</strong> —
                      the only ones Ad Strategist can actually analyze. Track up to 4.
                    </p>
                  )}
                  <button
                    onClick={handleSuggest}
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all cursor-pointer"
                  >
                    <i className="ri-sparkling-2-line" /> {suggestedOnce ? 'Try again' : 'Suggest'}
                  </button>
                </div>
              ) : suggestLoading ? (
                <div className="flex items-center justify-center gap-2 py-8 text-dark-muted text-sm">
                  <i className="ri-loader-4-line animate-spin text-primary text-lg" />
                  Finding competitors that run ads…
                  <span className="text-xs text-dark-muted/70 ml-auto">~15-30s</span>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-2">
                  {suggestions.map((s) => {
                    const isAdded = pickedQueries.has(s.query) || existingQueries.has(s.query);
                    return (
                      <div
                        key={s.query}
                        className="rounded-lg bg-white border border-surface-dark/30 px-3 py-2.5 flex items-start gap-2.5 hover:border-primary/40 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-dark truncate mb-0.5">{s.name}</p>
                          <p className="text-xs text-dark-muted line-clamp-2">{s.description}</p>
                          <span className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold border border-emerald-100">
                            <i className="ri-megaphone-line text-[10px]" /> Running ads
                          </span>
                        </div>
                        <button
                          onClick={() => handlePickSuggestion(s)}
                          disabled={isAdded}
                          className={`flex-shrink-0 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
                            isAdded
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default'
                              : 'bg-primary hover:bg-primary-hover text-white shadow-sm'
                          }`}
                        >
                          {isAdded ? (
                            <><i className="ri-check-line" /> Added</>
                          ) : (
                            <><i className="ri-add-line" /> Add</>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {suggestError && (
                <div className="mt-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                  {suggestError}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

